// src/components/TransaccionesPendientes.js

import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaSpinner, FaCheckCircle, FaTimesCircle, FaArrowLeft, FaArrowRight, FaSearch } from 'react-icons/fa';
import axios from 'axios';
import { fetchAuthSession } from 'aws-amplify/auth';
import Logo from './Logo';

// Función global para obtener headers con autenticación Cognito
const getAuthHeaders = async () => {
  try {
    const session = await fetchAuthSession();
    const idToken = session.tokens?.idToken?.toString();
    
    if (!idToken) {
      throw new Error('No se pudo obtener el ID token');
    }
    
    return {
      'Authorization': `Bearer ${idToken}`,
      'Content-Type': 'application/json'
    };
  } catch (error) {
    console.error('Error al obtener el token de autenticación:', error);
    throw error;
  }
};

// Función para formatear valores monetarios con separadores de miles
const formatCurrency = (value) => {
  if (value === null || value === undefined || value === '') return '$0';
  
  // Asegurar que value sea un número
  const numValue = typeof value === 'string' ? parseFloat(value.replace(/[^\d.-]/g, '')) : value;
  
  if (isNaN(numValue)) return '$0';
  
  // Formatear con separadores de miles
  return '$' + numValue.toLocaleString('es-CO');
};

// Componente de Modal de Confirmación
function ConfirmationModal({ isOpen, onClose, onConfirm, title, message, confirmLabel, cancelLabel, isLoading, type, showReasonInput, reasonValue, onReasonChange }) {
  if (!isOpen) return null;
  
  const modalStyle = type === 'approve' ? 'success' : 'danger';
  
  // Evitar que clicks en el modal se propaguen al fondo
  const handleModalClick = (e) => {
    e.stopPropagation();
  };
  
  return (
    <div className="confirmation-modal-overlay" onClick={onClose}>
      <div className="confirmation-modal" onClick={handleModalClick}>
        <div className={`confirmation-header ${modalStyle}`}>
          <h3>{title}</h3>
        </div>
        <div className="confirmation-body">
          <p>{message}</p>
          
          {showReasonInput && (
            <div className="reason-input-container">
              <label htmlFor="rejection-reason" className="reason-label">
                Por favor, indique la razón del rechazo:
              </label>
              <textarea
                id="rejection-reason"
                className="reason-textarea"
                value={reasonValue}
                onChange={onReasonChange}
                placeholder="Escriba aquí la razón del rechazo..."
                rows={4}
              />
              {reasonValue.trim() === '' && (
                <p className="reason-error">La razón del rechazo es obligatoria</p>
              )}
            </div>
          )}
        </div>
        <div className="confirmation-actions">
          <button 
            className="btn btn-outline" 
            onClick={onClose}
            disabled={isLoading}
          >
            {cancelLabel || 'Cancelar'}
          </button>
          <button 
            className={`btn btn-${modalStyle}`}
            onClick={onConfirm}
            disabled={isLoading || (showReasonInput && reasonValue.trim() === '')}
          >
            {isLoading ? <FaSpinner className="spin" /> : null}
            <span>{confirmLabel || 'Confirmar'}</span>
          </button>
        </div>
      </div>
      
      <style jsx>{`
        .confirmation-modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background-color: rgba(0, 0, 0, 0.7);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1100;
          padding: 20px;
        }
        
        .confirmation-modal {
          background: white;
          border-radius: 8px;
          width: 95%;
          max-width: 450px;
          overflow: hidden;
          box-shadow: 0 5px 20px rgba(0, 0, 0, 0.2);
          animation: fadeIn 0.2s ease-out;
        }
        
        .confirmation-header {
          padding: 16px 20px;
          border-bottom: 1px solid #eee;
        }
        
        .confirmation-header.success {
          background-color: rgba(16, 185, 129, 0.1);
          color: #10b981;
        }
        
        .confirmation-header.danger {
          background-color: rgba(239, 68, 68, 0.1);
          color: #ef4444;
        }
        
        .confirmation-header h3 {
          margin: 0;
          font-size: 1.2rem;
        }
        
        .confirmation-body {
          padding: 20px;
        }
        
        .reason-input-container {
          margin-top: 16px;
        }
        
        .reason-label {
          display: block;
          margin-bottom: 8px;
          font-weight: 500;
          color: #333;
        }
        
        .reason-textarea {
          width: 100%;
          padding: 10px;
          border: 1px solid #ddd;
          border-radius: 6px;
          font-family: inherit;
          font-size: 14px;
          resize: vertical;
          min-height: 80px;
        }
        
        .reason-textarea:focus {
          border-color: #ef4444;
          outline: none;
          box-shadow: 0 0 0 2px rgba(239, 68, 68, 0.2);
        }
        
        .reason-error {
          color: #ef4444;
          font-size: 0.85rem;
          margin-top: 6px;
          margin-bottom: 0;
        }
        
        .confirmation-actions {
          padding: 12px 20px 20px;
          display: flex;
          justify-content: flex-end;
          gap: 12px;
        }
        
        .btn-success {
          background-color: #10b981;
          color: white;
        }
        
        .btn-success:hover {
          background-color: #059669;
        }
        
        .btn-danger {
          background-color: #ef4444;
          color: white;
        }
        
        .btn-danger:hover {
          background-color: #dc2626;
        }
        
        .btn:disabled {
          opacity: 0.7;
          cursor: not-allowed;
        }
        
        .spin {
          animation: spin 1s linear infinite;
          margin-right: 8px;
        }
        
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
}

// Componente Modal para visualizar detalles de transacción
function TransactionDetailModal({ isOpen, onClose, transaction, onApprove, onReject }) {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isImageExpanded, setIsImageExpanded] = useState(false);
  const [activeTab, setActiveTab] = useState('info'); // 'info', 'device', 'customer', 'store', 'promoter'
  const [transactionImages, setTransactionImages] = useState([]);
  const [loadingImages, setLoadingImages] = useState(false);
  const [imageError, setImageError] = useState('');
  const modalRef = useRef(null);

  // Obtener las imágenes de la transacción desde la API
  useEffect(() => {
    const fetchTransactionImages = async () => {
      if (!isOpen || !transaction || !transaction.id) return;

      setLoadingImages(true);
      setImageError('');
      setTransactionImages([]); // Limpiar imágenes anteriores
      
      try {
        const headers = await getAuthHeaders();
        const response = await axios.get(
          'https://4svb468mc6.execute-api.us-east-2.amazonaws.com/prod/v1/info',
          {
            headers,
            params: {
              info_type: 'transactions_view_pending_transactions_images',
              tenant_id: localStorage.getItem('tenant_id') || '',
              transaction_id: transaction.id
            }
          }
        );

        if (response.status === 200 && response.data.pre_signed_urls) {
          setTransactionImages(response.data.pre_signed_urls);
          setCurrentImageIndex(0); // Resetear al primer índice
        } else {
          setImageError('No se encontraron imágenes para esta transacción.');
        }
      } catch (error) {
        console.error('Error al obtener imágenes de la transacción:', error);
        setImageError('Error al cargar las imágenes de la transacción.');
      } finally {
        setLoadingImages(false);
      }
    };

    fetchTransactionImages();
  }, [isOpen, transaction]);

  // Cerrar modal al hacer clic fuera
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (modalRef.current && !modalRef.current.contains(event.target) && !isImageExpanded) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, onClose, isImageExpanded]);

  // Manejar la navegación del carrusel
  const handlePrevImage = () => {
    if (transactionImages.length === 0) return;
    
    setCurrentImageIndex((prevIndex) => 
      prevIndex === 0 ? transactionImages.length - 1 : prevIndex - 1
    );
  };

  const handleNextImage = () => {
    if (transactionImages.length === 0) return;
    
    setCurrentImageIndex((prevIndex) => 
      prevIndex === transactionImages.length - 1 ? 0 : prevIndex + 1
    );
  };

  // Manejar la expansión de la imagen
  const toggleImageExpand = () => {
    if (transactionImages.length === 0) return;
    setIsImageExpanded(!isImageExpanded);
  };

  // Obtener datos originales
  const originalData = transaction.originalData || {};

  // Si el modal no está abierto, no renderizamos nada
  if (!isOpen) return null;

  return (
    <div className="modal-overlay">
      {/* Modal expandido de imagen */}
      {isImageExpanded && transactionImages.length > 0 && (
        <div className="expanded-image-overlay" onClick={toggleImageExpand}>
          <div className="expanded-image-container">
            <img 
              src={transactionImages[currentImageIndex]} 
              alt={`Imagen detallada de la transacción ${transaction.id}`} 
              className="expanded-image"
            />
            <button className="close-expanded-btn" onClick={toggleImageExpand}>×</button>
          </div>
        </div>
      )}

      {/* Modal principal */}
      <div className="modal-content transaction-detail-modal" ref={modalRef}>
        <div className="modal-header">
          <div className="modal-title-section">
            <h3>Detalles de Transacción #{transaction.id}</h3>
            <div className="transaction-status-badge">Pendiente</div>
          </div>
          <div className="modal-actions">
            <button 
              className="btn btn-success btn-sm"
              onClick={() => onApprove(transaction.id, originalData)}
            >
              <FaCheckCircle /> Aprobar
            </button>
            <button 
              className="btn btn-danger btn-sm"
              onClick={() => onReject(transaction.id, originalData)}
            >
              <FaTimesCircle /> Rechazar
            </button>
            <button className="modal-close-btn" onClick={onClose}>×</button>
          </div>
        </div>

        {/* Tabs de navegación */}
        <div className="modal-tabs">
          <button 
            className={`modal-tab ${activeTab === 'info' ? 'active' : ''}`}
            onClick={() => setActiveTab('info')}
          >
            Información General
          </button>
          <button 
            className={`modal-tab ${activeTab === 'device' ? 'active' : ''}`}
            onClick={() => setActiveTab('device')}
          >
            Dispositivo
          </button>
          <button 
            className={`modal-tab ${activeTab === 'customer' ? 'active' : ''}`}
            onClick={() => setActiveTab('customer')}
          >
            Cliente
          </button>
          <button 
            className={`modal-tab ${activeTab === 'store' ? 'active' : ''}`}
            onClick={() => setActiveTab('store')}
          >
            Tienda
          </button>
          <button 
            className={`modal-tab ${activeTab === 'promoter' ? 'active' : ''}`}
            onClick={() => setActiveTab('promoter')}
          >
            Promotor
          </button>
        </div>

        <div className="modal-body">
          {/* Contenido basado en la pestaña activa */}
          {activeTab === 'info' && (
            <div className="tab-content">
              <div className="tab-content-column">
                <div className="info-section">
                  <h4>Información de Transacción</h4>
                  <div className="info-grid">
                    <div className="info-item">
                      <span className="info-label">Estado:</span>
                      <span className="info-value status-pending">Pendiente</span>
                    </div>
                    <div className="info-item">
                      <span className="info-label"># Transacción:</span>
                      <span className="info-value">{originalData.transaction_id || '-'}</span>
                    </div>
                    <div className="info-item">
                      <span className="info-label">Fecha y hora:</span>
                      <span className="info-value">{transaction.date}</span>
                    </div>
                    <div className="info-item">
                      <span className="info-label">Promotor:</span>
                      <span className="info-value">{originalData.promoter_name || originalData.customer_advisor_name || '-'}</span>
                    </div>
                    <div className="info-item">
                      <span className="info-label">Nombre dispositivo (según promotor):</span>
                      <span className="info-value">{originalData.buyback_device_name_by_code || '-'}</span>
                    </div>
                    <div className="info-item">
                      <span className="info-label">Grado Final:</span>
                      <span className="info-value">{originalData.buyback_device_final_grade || '-'}</span>
                    </div>
                    <div className="info-item">
                      <span className="info-label">IMEI Principal:</span>
                      <span className="info-value">{originalData.buyback_device_imei || '-'}</span>
                    </div>
                    <div className="info-item">
                      <span className="info-label">IMEI Secundario:</span>
                      <span className="info-value">{originalData.buyback_device_imei_2 || '-'}</span>
                    </div>
                    <div className="info-item">
                      <span className="info-label">Bono Refurbi:</span>
                      <span className="info-value price">{formatCurrency(originalData.bonus_amount_trader || '0')}</span>
                    </div>
                    <div className="info-item">
                      <span className="info-label">Bono Campaña:</span>
                      <span className="info-value price">{formatCurrency(originalData.bonus_amount_campaign || '0')}</span>
                    </div>
                    <div className="info-item">
                      <span className="info-label">Bono Total:</span>
                      <span className="info-value price highlight">{formatCurrency(originalData.bonus_amount_total || '0')}</span>
                    </div>
                    <div className="info-item">
                      <span className="info-label">Moneda:</span>
                      <span className="info-value">{originalData.bonus_amount_currency || '-'}</span>
                    </div>
                  </div>
                </div>

                <div className="info-section reason-section">
                  <h4>Razón para Retoma Manual</h4>
                  <p className="transaction-notes">
                    {originalData.manual_form_reason || "No se especificó una razón para la retoma manual."}
                  </p>
                </div>
              </div>
              
              {/* Galería de imágenes */}
              <div className="image-gallery">
                <h4>Imágenes del Dispositivo</h4>
                
                {loadingImages ? (
                  <div className="loading-images">
                    <FaSpinner className="spin" />
                    <p>Cargando imágenes...</p>
                  </div>
                ) : imageError ? (
                  <div className="image-error">
                    <p>{imageError}</p>
                  </div>
                ) : transactionImages.length === 0 ? (
                  <div className="no-images">
                    <p>No hay imágenes disponibles para esta transacción.</p>
                  </div>
                ) : (
                  <div className="carousel-container">
                    {/* Imagen principal */}
                    <div className="main-image-container" onClick={toggleImageExpand}>
                      <img 
                        src={transactionImages[currentImageIndex]} 
                        alt={`Imagen ${currentImageIndex + 1} de la transacción`} 
                        className="main-image"
                      />
                      <div className="image-overlay">
                        <FaSearch className="zoom-icon" />
                        <span>Click para ampliar</span>
                      </div>
                    </div>

                    {/* Controles del carrusel */}
                    <div className="carousel-controls">
                      <button 
                        className="carousel-control" 
                        onClick={handlePrevImage}
                        aria-label="Imagen anterior"
                      >
                        <FaArrowLeft />
                      </button>
                      
                      <div className="carousel-indicators">
                        {transactionImages.map((_, index) => (
                          <button 
                            key={index}
                            className={`carousel-dot ${index === currentImageIndex ? 'active' : ''}`}
                            onClick={() => setCurrentImageIndex(index)}
                            aria-label={`Ir a imagen ${index + 1}`}
                          />
                        ))}
                      </div>
                      
                      <button 
                        className="carousel-control" 
                        onClick={handleNextImage}
                        aria-label="Imagen siguiente"
                      >
                        <FaArrowRight />
                      </button>
                    </div>

                    {/* Miniaturas */}
                    <div className="thumbnails">
                      {transactionImages.map((imageUrl, index) => (
                        <div 
                          key={index}
                          className={`thumbnail ${index === currentImageIndex ? 'active' : ''}`}
                          onClick={() => setCurrentImageIndex(index)}
                        >
                          <img src={imageUrl} alt={`Miniatura ${index + 1}`} />
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Pestaña de dispositivo */}
          {activeTab === 'device' && (
            <div className="tab-content single-column">
              <div className="info-section">
                <h4>Dispositivo Retomado</h4>
                <div className="info-grid">
                  <div className="info-item">
                    <span className="info-label">Nombre (según IMEI):</span>
                    <span className="info-value">{originalData.buyback_device_name_by_imei || '-'}</span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">Marca:</span>
                    <span className="info-value">{originalData.buyback_device_brand || originalData.buyback_device_brand_by_imei || '-'}</span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">Almacenamiento:</span>
                    <span className="info-value">{originalData.buyback_device_storage || '-'}</span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">IMEI Principal:</span>
                    <span className="info-value">{originalData.buyback_device_imei || '-'}</span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">IMEI Secundario:</span>
                    <span className="info-value">{originalData.buyback_device_imei_2 || '-'}</span>
                  </div>
                </div>
              </div>

              <div className="info-section">
                <h4>Evaluación del Dispositivo</h4>
                <div className="info-grid">
                  <div className="info-item">
                    <span className="info-label">Grado Cosmético:</span>
                    <span className="info-value grade">{originalData.buyback_device_cosmetic_grade || '-'}</span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">Grado Funcional:</span>
                    <span className="info-value grade">{originalData.buyback_device_functional_grade || '-'}</span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">Grado Final:</span>
                    <span className="info-value grade highlight">{originalData.buyback_device_final_grade || '-'}</span>
                  </div>
                </div>
              </div>

              <div className="info-section">
                <h4>Dispositivo Adquirido</h4>
                <div className="info-grid">
                  <div className="info-item">
                    <span className="info-label">Nombre:</span>
                    <span className="info-value">{originalData.sold_device_name || '-'}</span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">Precio:</span>
                    <span className="info-value price">{formatCurrency(originalData.sold_device_price || '0')}</span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">Almacenamiento:</span>
                    <span className="info-value">{originalData.sold_device_storage || '-'} GB</span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Pestaña de cliente */}
          {activeTab === 'customer' && (
            <div className="tab-content single-column">
              <div className="info-section">
                <h4>Información del Cliente</h4>
                <div className="info-grid">
                  <div className="info-item">
                    <span className="info-label">Nombre:</span>
                    <span className="info-value">{originalData.customer_customer_name || '-'}</span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">Apellidos:</span>
                    <span className="info-value">{originalData.customer_customer_surname || '-'}</span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">Cédula:</span>
                    <span className="info-value">{originalData.customer_customer_id || '-'}</span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">Teléfono:</span>
                    <span className="info-value">{originalData.customer_customer_phone || '-'}</span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">Email:</span>
                    <span className="info-value">{originalData.customer_customer_email || '-'}</span>
                  </div>
                </div>
              </div>

              <div className="info-section">
                <h4>Dirección</h4>
                <div className="info-grid">
                  <div className="info-item">
                    <span className="info-label">Dirección:</span>
                    <span className="info-value">{originalData.customer_customer_address || '-'}</span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">Ciudad:</span>
                    <span className="info-value">{originalData.customer_customer_city || '-'}</span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">Departamento:</span>
                    <span className="info-value">{originalData.customer_customer_department || '-'}</span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Pestaña de tienda */}
          {activeTab === 'store' && (
            <div className="tab-content single-column">
              <div className="info-section">
                <h4>Información de la Tienda</h4>
                <div className="info-grid">
                  <div className="info-item">
                    <span className="info-label">Retailer:</span>
                    <span className="info-value">{originalData.retailer || '-'}</span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">Marca:</span>
                    <span className="info-value">{originalData.brand || '-'}</span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">Tienda:</span>
                    <span className="info-value">{originalData.store_name || '-'}</span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">Ciudad:</span>
                    <span className="info-value">{originalData.store_city || '-'}</span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">Compañía:</span>
                    <span className="info-value">{originalData.company_name || '-'}</span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Nueva pestaña de promotor */}
          {activeTab === 'promoter' && (
            <div className="tab-content single-column">
              <div className="info-section">
                <h4>Información del Promotor</h4>
                <div className="info-grid">
                  <div className="info-item">
                    <span className="info-label">Nombre:</span>
                    <span className="info-value">{originalData.promoter_name || originalData.customer_advisor_name || '-'}</span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">Email:</span>
                    <span className="info-value">{originalData.promoter_email || '-'}</span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">Cédula:</span>
                    <span className="info-value">{originalData.promoter_id || originalData.customer_advisor_id || '-'}</span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">Tipo:</span>
                    <span className="info-value">{originalData.promoter_type || '-'}</span>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      <style jsx>{`
        .modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background-color: rgba(0, 0, 0, 0.6);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
          padding: 20px;
        }
        
        .transaction-detail-modal {
          background: white;
          border-radius: 12px;
          box-shadow: 0 10px 25px rgba(0, 0, 0, 0.2);
          width: 95%;
          max-width: 1200px;
          max-height: 90vh;
          overflow: hidden;
          display: flex;
          flex-direction: column;
          animation: fadeIn 0.3s ease-out;
        }
        
        .modal-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 16px 24px;
          border-bottom: 1px solid #eaeaea;
          background: linear-gradient(135deg, var(--primary-color), var(--primary-dark));
          color: white;
        }

        .modal-title-section {
          display: flex;
          align-items: center;
          gap: 12px;
        }
        
        .transaction-status-badge {
          background: #fef3c7;
          color: #d97706;
          padding: 4px 12px;
          border-radius: 100px;
          font-size: 0.85rem;
          font-weight: 500;
        }
        
        .modal-actions {
          display: flex;
          align-items: center;
          gap: 10px;
        }
        
        .modal-header h3 {
          margin: 0;
          font-size: 1.4rem;
          color: white;
        }
        
        .modal-close-btn {
          background: none;
          border: none;
          font-size: 24px;
          cursor: pointer;
          color: white;
          opacity: 0.8;
          transition: opacity 0.2s;
          margin-left: 10px;
        }
        
        .modal-close-btn:hover {
          opacity: 1;
        }

        .btn-sm {
          padding: 8px 16px;
          font-size: 0.9rem;
        }
        
        .modal-tabs {
          display: flex;
          background: #f5f7fa;
          border-bottom: 1px solid #eaeaea;
          padding: 0 24px;
          overflow-x: auto;
          -webkit-overflow-scrolling: touch;
          scrollbar-width: thin;
          min-height: 35px;
        }
        
        .modal-tab {
          padding: 12px 20px;
          background: none;
          border: none;
          border-bottom: 3px solid transparent;
          cursor: pointer;
          font-size: 0.95rem;
          font-weight: 500;
          color: #555;
          transition: all 0.2s;
          white-space: nowrap;
          flex-shrink: 0;
          min-width: fit-content;
        }
        
        .modal-tab:hover {
          color: #333;
        }
        
        .modal-tab.active {
          color: var(--primary-color);
          border-bottom-color: var(--primary-color);
          background-color: rgba(99, 102, 241, 0.05);
        }
        
        .modal-tab:active {
          background-color: rgba(99, 102, 241, 0.1);
          transform: scale(0.98);
        }
        
        .modal-body {
          padding: 24px;
          overflow-y: auto;
        }
        
        .tab-content {
          display: flex;
          gap: 30px;
        }
        
        .tab-content-column {
          flex: 1;
          min-width: 300px;
        }
        
        .single-column {
          max-width: 800px;
          margin: 0 auto;
        }
        
        .info-section {
          margin-bottom: 24px;
          background: #f9f9f9;
          padding: 16px;
          border-radius: 8px;
        }
        
        .reason-section {
          background: #fff8e6;
          border-left: 4px solid #f59e0b;
        }
        
        .info-section h4 {
          margin-top: 0;
          margin-bottom: 16px;
          color: #333;
          font-size: 1.1rem;
          border-bottom: 1px solid #eee;
          padding-bottom: 8px;
        }
        
        .info-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
          gap: 16px;
        }
        
        .info-item {
          display: flex;
          flex-direction: column;
        }
        
        .info-label {
          font-weight: 600;
          color: #555;
          margin-bottom: 4px;
          font-size: 0.9rem;
        }
        
        .info-value {
          font-size: 1rem;
          word-break: break-word;
        }
        
        .detail-value {
          font-weight: 500;
          color: #333;
          word-break: break-word;
          max-height: none;
          overflow: visible;
          text-overflow: initial;
          display: block;
        }
        
        .transaction-detail:not(:nth-child(2)) .detail-value {
          max-height: 40px;
          overflow: hidden;
          text-overflow: ellipsis;
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
        }
        
        .detail-value.price {
          color: #059669;
          font-weight: 600;
        }
        
        .status-pending {
          color: #f59e0b;
          font-weight: 600;
        }
        
        .price {
          color: #059669;
          font-weight: 600;
        }
        
        .grade {
          font-weight: 600;
        }
        
        .highlight {
          font-size: 1.1rem;
          background: #e6fffa;
          padding: 2px 6px;
          border-radius: 4px;
        }
        
        .transaction-notes {
          background: #fff;
          padding: 12px;
          border-radius: 6px;
          border: 1px solid #eee;
          font-size: 0.95rem;
          color: #555;
        }
        
        .verification-actions {
          display: flex;
          gap: 12px;
          margin-top: 24px;
        }
        
        .btn {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          padding: 10px 20px;
          border-radius: 8px;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s;
          border: none;
          font-size: 1rem;
        }
        
        .btn-success {
          background-color: #10b981;
          color: white;
        }
        
        .btn-success:hover {
          background-color: #059669;
        }
        
        .btn-danger {
          background-color: #ef4444;
          color: white;
        }
        
        .btn-danger:hover {
          background-color: #dc2626;
        }
        
        .image-gallery {
          flex: 1;
          min-width: 300px;
        }
        
        .image-gallery h4 {
          margin-top: 0;
          margin-bottom: 16px;
          color: #333;
        }
        
        .carousel-container {
          display: flex;
          flex-direction: column;
          gap: 12px;
        }
        
        .main-image-container {
          position: relative;
          cursor: pointer;
          border-radius: 8px;
          overflow: hidden;
          height: 0;
          padding-bottom: 70%;
        }
        
        .main-image {
          position: absolute;
          width: 100%;
          height: 100%;
          object-fit: cover;
          object-position: top;
          transition: transform 0.3s ease;
        }
        
        .main-image-container:hover .main-image {
          transform: scale(1.02);
        }
        
        .image-overlay {
          position: absolute;
          bottom: 0;
          left: 0;
          right: 0;
          background: rgba(0, 0, 0, 0.6);
          color: white;
          padding: 8px 16px;
          display: flex;
          align-items: center;
          gap: 8px;
          font-size: 0.9rem;
        }
        
        .zoom-icon {
          font-size: 1.1rem;
        }
        
        .carousel-controls {
          display: flex;
          align-items: center;
          justify-content: space-between;
        }
        
        .carousel-control {
          background: var(--primary-light);
          color: white;
          border: none;
          width: 36px;
          height: 36px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: background 0.2s;
        }
        
        .carousel-control:hover {
          background: var(--primary-dark);
        }
        
        .carousel-indicators {
          display: flex;
          gap: 8px;
        }
        
        .carousel-dot {
          width: 12px;
          height: 12px;
          border-radius: 50%;
          background: #ddd;
          border: none;
          cursor: pointer;
          transition: background 0.2s, transform 0.2s;
        }
        
        .carousel-dot.active {
          background: var(--primary-color);
          transform: scale(1.2);
        }
        
        .thumbnails {
          display: flex;
          gap: 8px;
          overflow-x: auto;
          padding: 4px 0;
        }
        
        .thumbnail {
          width: 80px;
          height: 60px;
          border-radius: 4px;
          overflow: hidden;
          cursor: pointer;
          opacity: 0.7;
          transition: opacity 0.2s, transform 0.2s;
          flex-shrink: 0;
        }
        
        .thumbnail:hover {
          opacity: 0.9;
        }
        
        .thumbnail.active {
          opacity: 1;
          box-shadow: 0 0 0 2px var(--primary-color);
          transform: scale(1.05);
        }
        
        .thumbnail img {
          width: 100%;
          height: 100%;
          object-fit: cover;
          object-position: top;
        }
        
        .expanded-image-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background-color: rgba(0, 0, 0, 0.9);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1100;
          cursor: zoom-out;
        }
        
        .expanded-image-container {
          position: relative;
          width: 90%;
          max-width: 1200px;
          height: 90vh;
        }
        
        .expanded-image {
          width: 100%;
          height: 100%;
          object-fit: contain;
        }
        
        .close-expanded-btn {
          position: absolute;
          top: 20px;
          right: 20px;
          background: rgba(0, 0, 0, 0.5);
          color: white;
          border: none;
          width: 40px;
          height: 40px;
          border-radius: 50%;
          font-size: 24px;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: background 0.2s;
        }
        
        .close-expanded-btn:hover {
          background: rgba(0, 0, 0, 0.8);
        }
        
        .loading-images {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 40px 0;
          color: #666;
          background-color: #f9f9f9;
          border-radius: 8px;
        }
        
        .loading-images .spin {
          font-size: 2rem;
          margin-bottom: 1rem;
          animation: spin 1s linear infinite;
        }
        
        .image-error, .no-images {
          padding: 30px;
          text-align: center;
          background-color: #f9f9f9;
          border-radius: 8px;
          color: #666;
        }
        
        .image-error {
          color: #ef4444;
          border-left: 4px solid #ef4444;
        }
        
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        
        @media (max-width: 992px) {
          .tab-content {
            flex-direction: column;
          }
          
          .transaction-detail-modal {
            max-height: 95vh;
          }
          
          .modal-tabs {
            padding: 0 12px;
          }
          
          .modal-tab {
            padding: 12px 16px;
          }
          
          .modal-header {
            flex-direction: column;
            align-items: flex-start;
            gap: 10px;
          }
          
          .modal-actions {
            align-self: flex-end;
          }
        }
        
        @media (max-width: 576px) {
          .info-grid {
            grid-template-columns: 1fr;
          }
          
          .verification-actions {
            flex-direction: column;
          }
          
          .modal-body {
            padding: 16px;
          }
          
          .modal-tabs {
            position: relative;
            padding: 0 8px;
            min-height: 35px;
          }
          
          .modal-tab {
            padding: 12px 12px;
          }
          
          .modal-header {
            padding: 12px 16px;
          }
          
          .modal-header h3 {
            font-size: 1.1rem;
          }
          
          .btn-sm {
            padding: 6px 12px;
            font-size: 0.8rem;
          }
          
          .transaction-status-badge {
            font-size: 0.75rem;
            padding: 2px 8px;
          }
          
          .transaction-detail-modal {
            max-height: 92vh;
            margin-top: 20px;
          }
        }
      `}</style>
    </div>
  );
}

// Componente principal TransaccionesPendientes
function TransaccionesPendientes() {
  const navigate = useNavigate();
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [retryCount, setRetryCount] = useState(0);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [confirmAction, setConfirmAction] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const [actionTransaction, setActionTransaction] = useState(null);
  const [actionReason, setActionReason] = useState('');
  const [actionSuccess, setActionSuccess] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  const tenantId = localStorage.getItem('tenant_id') || '';

  // Cargar transacciones pendientes
  useEffect(() => {
    fetchPendingTransactions();
  }, []);

  const fetchPendingTransactions = async () => {
    setLoading(true);
    setError('');

    let params = {
      info_type: 'transactions_view_pending_transactions',
      tenant_id: tenantId
    };

    try {
      //console.log("Fetching pending transactions with params:", params);
      const headers = await getAuthHeaders();
      const response = await axios.get(
        'https://4svb468mc6.execute-api.us-east-2.amazonaws.com/prod/v1/info',
        {
          headers,
          params
        }
      );

      if (response.status === 200) {
        if (response.data.transactions && response.data.transactions.length > 0) {
          //console.log("Received transactions:", response.data.transactions);
          setTransactions(response.data.transactions);
        } else {
          setTransactions([]);
          setError('No hay transacciones pendientes en este momento.');
        }
        setRetryCount(0);
      }
    } catch (error) {
      console.error('Error al obtener transacciones pendientes:', error);
      setError('Error al cargar transacciones pendientes. Inténtalo de nuevo.');
      if (retryCount < 3) {
        setRetryCount(prevCount => prevCount + 1);
        setTimeout(() => fetchPendingTransactions(), 1000);
      } else {
        setError('No se pudieron cargar las transacciones después de varios intentos.');
      }
    } finally {
      setLoading(false);
    }
  };

  // Función para formatear fechas ISO
  const formatDate = (isoDate) => {
    if (!isoDate) return '-';
    
    try {
      const date = new Date(isoDate);
      if (isNaN(date.getTime())) {
        return isoDate; // Si no es una fecha válida, retornar el string original
      }
      
      // Formatear fecha: DD/MM/YYYY HH:MM
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      
      return `${day}/${month}/${year} ${hours}:${minutes}`;
    } catch (e) {
      console.error('Error al formatear fecha:', e);
      return isoDate;
    }
  };

  // Filtrar transacciones por término de búsqueda
  const filteredTransactions = transactions.filter((transaction) => {
    if (!searchTerm) return true;
    
    const searchLower = searchTerm.toLowerCase();
    // Buscar en múltiples campos
    return (
      (transaction.transaction_id && transaction.transaction_id.toLowerCase().includes(searchLower)) ||
      (transaction.customer_customer_name && transaction.customer_customer_name.toLowerCase().includes(searchLower)) ||
      (transaction.customer_customer_id && transaction.customer_customer_id.toLowerCase().includes(searchLower)) ||
      (transaction.buyback_device_imei && transaction.buyback_device_imei.toLowerCase().includes(searchLower)) ||
      (transaction.buyback_device_name_by_imei && transaction.buyback_device_name_by_imei.toLowerCase().includes(searchLower)) ||
      (transaction.store_name && transaction.store_name.toLowerCase().includes(searchLower))
    );
  });

  // Preparar datos para las tarjetas de transacción
  const prepareTransactionCardData = (transaction) => {
    return {
      id: transaction.transaction_id || 'Sin ID',
      date: formatDate(transaction.timestamp_iso) || 'Sin fecha',
      customer: `${transaction.customer_customer_name || ''} ${transaction.customer_customer_surname || ''}`.trim() || 'Sin nombre',
      device: transaction.buyback_device_name_by_imei || transaction.buyback_device_name_by_code || 'Sin info',
      imei: transaction.buyback_device_imei || 'Sin IMEI',
      value: transaction.bonus_amount_total || '0.00',
      store: transaction.store_name || 'Sin tienda',
      status: 'pending',
      promoter: transaction.promoter_name || 'Sin promotor',
      originalData: transaction // Guardamos los datos originales para el modal
    };
  };

  // Abrir modal con detalles de transacción
  const openTransactionDetails = (transaction) => {
    setSelectedTransaction(transaction);
    setIsModalOpen(true);
  };

  // Cerrar modal
  const closeModal = () => {
    setIsModalOpen(false);
  };

  // Funciones para manejar aprobación/rechazo
  const handleApproveRequest = (transactionId, transactionData) => {
    //console.log('Datos para aprobación:', transactionData); // Log de diagnóstico
    //console.log('Valor de retailer en aprobación:', transactionData.retailer);
    
    setActionTransaction({
      transaction_id: transactionId,
      originalData: transactionData,  // Asegurarnos que originalData esté disponible
      retailer: transactionData.retailer // Guardar explícitamente el retailer
    });
    setConfirmAction('approve');
    setActionReason(''); // Limpiar razón anterior
    setConfirmModalOpen(true);
  };

  const handleRejectRequest = (transactionId, transactionData) => {
    //console.log('Datos para rechazo:', transactionData); // Log de diagnóstico
    //console.log('Valor de retailer en rechazo:', transactionData.retailer);
    
    setActionTransaction({
      transaction_id: transactionId,
      originalData: transactionData,  // Asegurarnos que originalData esté disponible
      retailer: transactionData.retailer // Guardar explícitamente el retailer
    });
    setConfirmAction('reject');
    setActionReason(''); // Limpiar razón anterior
    setConfirmModalOpen(true);
  };
  
  const closeConfirmModal = () => {
    setConfirmModalOpen(false);
    setActionLoading(false);
    // NO eliminamos actionTransaction aquí para evitar perder los datos
    setActionReason('');
  };
  
  const processTransactionAction = async () => {
    if (!actionTransaction || !actionTransaction.transaction_id) {
      setError('No se pudo identificar la transacción.');
      return;
    }
    
    // Validar que haya una razón para el rechazo
    if (confirmAction === 'reject' && actionReason.trim() === '') {
      return; // No seguir si no hay razón de rechazo (el botón ya debería estar deshabilitado)
    }
    
    // Diagnóstico de los datos recibidos
    //console.log('Datos completos de actionTransaction:', actionTransaction);
    
    setActionLoading(true);
    
    try {
      // Obtener información del usuario actual
      const reviewerName = localStorage.getItem('user_name') || 'Usuario';
      const reviewerId = localStorage.getItem('user_id') || tenantId; // Usar el tenant_id como fallback si no hay user_id
      
      // Obtener timestamp actual en formato epoch (segundos)
      const reviewTimestamp = Math.floor(Date.now() / 1000);
      
      // Obtener retailer de la transacción
      const retailerValue = actionTransaction.retailer || 
                           actionTransaction.originalData?.retailer || 
                           (actionTransaction.originalData && typeof actionTransaction.originalData === 'object' ? 
                              Object.values(actionTransaction.originalData).find(val => 
                                typeof val === 'string' && val.match(/^(CLARO|TIGO|MOVISTAR|ETB|WOM)$/i)
                              ) : '') || 
                           '';
      
      //console.log('Valor de retailer a enviar:', retailerValue);
      
      // Datos para la actualización del estado de la transacción
      const data = {
        tenant_id: tenantId,
        retailer: retailerValue,
        transaction_id: actionTransaction.transaction_id,
        tradein_step: confirmAction === 'approve' ? 'manual_tradein_approved' : 'manual_tradein_rejected',
        review_timestamp: reviewTimestamp,
        reviewer_id: reviewerId,
        reviewer_name: reviewerName,
        rejection_reason: confirmAction === 'reject' ? actionReason : ''
      };
      
      //console.log("Enviando actualización de estado de transacción:", data);
      
      // Realizar la solicitud PATCH al nuevo endpoint
      const headers = await getAuthHeaders();
      const response = await axios.patch(
        'https://4svb468mc6.execute-api.us-east-2.amazonaws.com/prod/v1/transactions',
        data,
        { headers }
      );
      
      // Manejar la respuesta
      if (response.status === 200 || response.status === 201 || response.status === 202 || response.status === 204) {
        setActionSuccess(true);
        
        // Usar el mensaje de la API si está disponible
        if (response.data && response.data.message) {
          setSuccessMessage(response.data.message);
        } else {
          setSuccessMessage(
            confirmAction === 'approve' 
              ? 'Transacción aprobada correctamente.' 
              : 'Transacción rechazada correctamente.'
          );
        }
        
        // Registrar información adicional de la respuesta
        //console.log('Respuesta exitosa de la API:', response.data);
        
        // Remover la transacción de la lista
        setTransactions(prev => prev.filter(t => t.transaction_id !== actionTransaction.transaction_id));
        
        // Cerrar ambos modales
        closeConfirmModal();
        setIsModalOpen(false);
      } else {
        throw new Error(`La respuesta del servidor no fue exitosa: ${response.status}`);
      }
    } catch (error) {
      console.error('Error al procesar la acción:', error);
      
      // Intentar extraer un mensaje de error más detallado
      let errorMessage = `Error al ${confirmAction === 'approve' ? 'aprobar' : 'rechazar'} la transacción`;
      
      if (error.response) {
        // La API respondió con un código de error
        console.error('Respuesta de error de la API:', error.response.data);
        
        if (error.response.data && error.response.data.message) {
          errorMessage = error.response.data.message;
        } else {
          errorMessage += `: ${error.response.status} ${error.response.statusText}`;
        }
      } else if (error.message) {
        // Error de red u otro tipo de error
        errorMessage += `: ${error.message}`;
      }
      
      setError(errorMessage);
    } finally {
      setActionLoading(false);
    }
  };
  
  // Mostrar mensaje de éxito temporal
  useEffect(() => {
    if (actionSuccess) {
      const timer = setTimeout(() => {
        setActionSuccess(false);
        setSuccessMessage('');
      }, 3000);
      
      return () => clearTimeout(timer);
    }
  }, [actionSuccess]);

  return (
    <div className="transactions-pending-container">
      <header className="header">
        <Logo 
          variant="horizontal" 
          size="medium" 
          onClick={() => navigate('/dashboard')} 
        />
        <div className="header-title">Transacciones Pendientes</div>
        <div className="user-menu">
          <button className="btn btn-outline" onClick={() => navigate('/dashboard')}>Volver al Dashboard</button>
        </div>
      </header>

      <div className="container">
        <div className="card">
          <div className="card-header">
            <h2 className="card-title">Transacciones Pendientes de Verificación</h2>
            <div className="search-container">
              <input
                type="text"
                className="search-input"
                placeholder="Buscar por ID, cliente, dispositivo o IMEI..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              <FaSearch className="search-icon" />
            </div>
          </div>

          {loading ? (
            <div className="loading-container">
              <FaSpinner className="loading-spinner" />
              <p>Cargando transacciones pendientes...</p>
            </div>
          ) : error ? (
            <div className="error-message">
              <p>{error}</p>
              <button className="btn btn-primary" onClick={() => fetchPendingTransactions()}>
                Reintentar
              </button>
            </div>
          ) : filteredTransactions.length === 0 ? (
            <div className="empty-state">
              {searchTerm ? (
                <p>No se encontraron transacciones que coincidan con "{searchTerm}"</p>
              ) : (
                <p>No hay transacciones pendientes de verificación</p>
              )}
            </div>
          ) : (
            <div className="transactions-list">
              {filteredTransactions.map((transaction) => {
                const cardData = prepareTransactionCardData(transaction);
                return (
                  <div 
                    key={transaction.transaction_id || Math.random().toString()} 
                    className="transaction-card"
                    onClick={() => openTransactionDetails(transaction)}
                  >
                    <div className="transaction-header">
                      <div className="transaction-id">{cardData.id}</div>
                      <div className="transaction-status">Pendiente</div>
                    </div>
                    
                    <div className="transaction-body">
                      <div className="transaction-detail">
                        <span className="detail-label">Fecha:</span>
                        <span className="detail-value">{cardData.date}</span>
                      </div>
                      <div className="transaction-detail">
                        <span className="detail-label">Cliente:</span>
                        <span className="detail-value">{cardData.customer}</span>
                      </div>
                      <div className="transaction-detail">
                        <span className="detail-label">Dispositivo:</span>
                        <span className="detail-value">{cardData.device}</span>
                      </div>
                      <div className="transaction-detail">
                        <span className="detail-label">IMEI:</span>
                        <span className="detail-value">{cardData.imei}</span>
                      </div>
                      <div className="transaction-detail">
                        <span className="detail-label">Valor:</span>
                        <span className="detail-value price">{formatCurrency(cardData.value)}</span>
                      </div>
                      <div className="transaction-detail">
                        <span className="detail-label">Tienda:</span>
                        <span className="detail-value">{cardData.store}</span>
                      </div>
                    </div>
                    
                    <div className="transaction-footer">
                      <button className="btn btn-primary view-details-btn">
                        Ver Detalles y Verificar
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Modal de éxito para acciones de aprobación/rechazo */}
      {actionSuccess && (
        <div className="success-notification">
          <div className="success-icon">✓</div>
          <p>{successMessage}</p>
        </div>
      )}

      {/* Modal de detalles */}
      {selectedTransaction && (
        <TransactionDetailModal 
          isOpen={isModalOpen}
          onClose={closeModal}
          transaction={prepareTransactionCardData(selectedTransaction)}
          onApprove={handleApproveRequest}
          onReject={handleRejectRequest}
        />
      )}

      {/* Modal de confirmación para aprobación/rechazo */}
      <ConfirmationModal
        isOpen={confirmModalOpen}
        onClose={closeConfirmModal}
        onConfirm={processTransactionAction}
        title={confirmAction === 'approve' ? '¿Aprobar esta transacción?' : '¿Rechazar esta transacción?'}
        message={
          confirmAction === 'approve' 
            ? '¿Está seguro que desea aprobar esta transacción? Esta acción no se puede deshacer.'
            : '¿Está seguro que desea rechazar esta transacción? Esta acción no se puede deshacer.'
        }
        confirmLabel={confirmAction === 'approve' ? 'Aprobar' : 'Rechazar'}
        cancelLabel="Cancelar"
        isLoading={actionLoading}
        type={confirmAction}
        showReasonInput={confirmAction === 'reject'}
        reasonValue={actionReason}
        onReasonChange={(e) => setActionReason(e.target.value)}
      />

      <style jsx>{`
        .transactions-pending-container {
          min-height: 100vh;
          background-color: var(--background-light);
        }
        
        .container {
          max-width: 1200px;
          margin: 2rem auto;
          padding: 0 1.5rem;
        }
        
        .card {
          background-color: white;
          border-radius: 12px;
          box-shadow: var(--shadow-md);
          padding: 2rem;
          transition: var(--transition);
        }
        
        .card-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          flex-wrap: wrap;
          gap: 1rem;
          margin-bottom: 2rem;
        }
        
        .card-title {
          margin: 0;
          font-size: 1.5rem;
          color: var(--primary-color);
        }
        
        .search-container {
          position: relative;
          min-width: 300px;
        }
        
        .search-input {
          width: 100%;
          padding: 0.75rem 1rem 0.75rem 2.5rem;
          border: 1px solid #e1e1e1;
          border-radius: 8px;
          font-size: 1rem;
          transition: all 0.3s ease;
        }
        
        .search-input:focus {
          border-color: var(--primary-color);
          box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.2);
          outline: none;
        }
        
        .search-icon {
          position: absolute;
          left: 0.8rem;
          top: 50%;
          transform: translateY(-50%);
          color: #999;
        }
        
        .loading-container {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 3rem 0;
          color: var(--text-light);
        }
        
        .loading-spinner {
          animation: spin 1s linear infinite;
          color: var(--primary-color);
          font-size: 2rem;
          margin-bottom: 1rem;
        }
        
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        
        .error-message {
          text-align: center;
          color: #e53e3e;
          padding: 2rem;
        }
        
        .empty-state {
          text-align: center;
          color: var(--text-light);
          padding: 3rem 0;
        }
        
        .transactions-list {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
          gap: 1.5rem;
          grid-auto-rows: 1fr;
        }
        
        .transaction-card {
          background-color: #f9f9f9;
          border-radius: 10px;
          border: 1px solid #eee;
          overflow: hidden;
          transition: all 0.3s ease;
          cursor: pointer;
          display: flex;
          flex-direction: column;
          height: 100%;
          position: relative;
        }
        
        .transaction-content {
          flex: 1;
          display: flex;
          flex-direction: column;
        }
        
        .transaction-card:hover {
          transform: translateY(-5px);
          box-shadow: 0 10px 15px rgba(0, 0, 0, 0.1);
          border-color: #ddd;
        }
        
        .transaction-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 0.75rem 1rem;
          background-color: #f3f4f6;
          border-bottom: 1px solid #eee;
        }
        
        .transaction-id {
          font-weight: 600;
          color: var(--primary-color);
        }
        
        .transaction-status {
          background-color: #fef3c7;
          color: #d97706;
          padding: 4px 10px;
          border-radius: 100px;
          font-size: 0.85rem;
          font-weight: 500;
        }
        
        .transaction-body {
          padding: 1rem;
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 0.75rem;
          flex: 1;
          min-height: 220px; /* Altura mínima para mantener la uniformidad */
        }
        
        .transaction-footer {
          padding: 0;
          position: relative;
        }
        
        .transaction-detail {
          display: flex;
          flex-direction: column;
          margin-bottom: 6px;
        }
        
        .detail-label {
          font-size: 0.85rem;
          color: #666;
          margin-bottom: 4px;
        }
        
        .detail-value {
          font-weight: 500;
          color: #333;
          word-break: break-word;
          max-height: none;
          overflow: visible;
          text-overflow: initial;
          display: block;
        }
        
        .transaction-detail:not(:nth-child(2)) .detail-value {
          max-height: 40px;
          overflow: hidden;
          text-overflow: ellipsis;
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
        }
        
        .view-details-btn {
          width: 100%;
          padding: 0.75rem;
          border: none;
          background-color: var(--primary-color);
          color: white;
          font-weight: 500;
          cursor: pointer;
          transition: background-color 0.2s;
          margin-top: auto;
          border-radius: 0;
        }
        
        .view-details-btn:hover {
          background-color: var(--primary-dark);
        }
        
        @media (max-width: 768px) {
          .card-header {
            flex-direction: column;
            align-items: flex-start;
          }
          
          .search-container {
            width: 100%;
          }
          
          .transactions-list {
            grid-template-columns: 1fr;
          }
        }
        
        .success-notification {
          position: fixed;
          top: 20px;
          right: 20px;
          background: #10b981;
          color: white;
          padding: 12px 20px;
          border-radius: 8px;
          box-shadow: 0 3px 10px rgba(0, 0, 0, 0.2);
          display: flex;
          align-items: center;
          gap: 12px;
          z-index: 1200;
          animation: slideIn 0.3s ease-out;
        }
        
        .success-icon {
          background: white;
          color: #10b981;
          width: 24px;
          height: 24px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: bold;
        }
        
        @keyframes slideIn {
          from { transform: translateX(100%); opacity: 0; }
          to { transform: translateX(0); opacity: 1; }
        }
      `}</style>
    </div>
  );
}

export default TransaccionesPendientes; 